<template>
    <div>
      <!-- Modal Backdrop -->
      <div v-if="isOpen" class="modal-backdrop" @click="closeModal"></div>
      
      <!-- Modal -->
      <div v-if="isOpen" class="token-modal" role="dialog" aria-modal="true">
        <div class="modal-content-wrapper">
          <!-- Close Button -->
          <button class="close-btn" @click="closeModal" aria-label="Close">
            <i class="fa-solid fa-times"></i>
          </button>
  
          <!-- Stripe Logo -->
          <div class="text-center mb-4">
            <img src="images/stripe.png" style="width: 150px;" alt="Stripe">
          </div>
  
          <!-- Token Purchase Form -->
          <form @submit.prevent="submitPayment" class="payment-form">
            <h4 class="text-center mb-4">Purchase Tokens</h4>
  
            <!-- Token Package Selection -->
            <div class="form-group">
              <label for="token-package">Select Token Package</label>
              <select 
                id="token-package" 
                v-model="selectedPackage" 
                @change="onPackageChange"
                class="form-control"
                required
              >
                <option value="" disabled>Choose a package</option>
                <option 
                  v-for="pkg in tokenPackages" 
                  :key="pkg.tokens" 
                  :value="pkg"
                >
                  {{ pkg.tokens }} Tokens - ${{ pkg.amount.toFixed(2) }}
                </option>
              </select>
            </div>
  
            <!-- Display Selected Package Info -->
            <div v-if="selectedPackage" class="package-info">
              <div class="info-row">
                <span>Tokens:</span>
                <strong>{{ selectedPackage.tokens }}</strong>
              </div>
              <div class="info-row">
                <span>Amount:</span>
                <strong>${{ selectedPackage.amount.toFixed(2) }}</strong>
              </div>
            </div>
  
            <!-- Card Number -->
            <div class="form-group">
              <label for="card-number-element">Card Number</label>
              <div id="card-number-element-modal" class="card-element"></div>
            </div>
  
            <!-- Expiry Date -->
            <div class="form-group">
              <label for="card-expiry-element">Expiry Date</label>
              <div id="card-expiry-element-modal" class="card-element"></div>
            </div>
  
            <!-- CVC -->
            <div class="form-group">
              <label for="card-cvc-element">CVC</label>
              <div id="card-cvc-element-modal" class="card-element"></div>
            </div>
  
            <!-- Error Messages -->
            <div v-if="cardErrors" class="card-errors" role="alert">
              {{ cardErrors }}
            </div>
  
            <!-- Submit Button -->
            <button type="submit" class="submit-button" :disabled="loader || !selectedPackage">
              <loader v-if="loader" />
              <span v-if="!loader">Pay Now</span>
            </button>
          </form>
        </div>
      </div>
  
      <!-- Success Modal -->
      <div v-if="showSuccessModal" class="modal-backdrop"></div>
      <div v-if="showSuccessModal" class="success-modal" role="dialog" aria-modal="true">
        <div class="modal-dialog modal-dialog-centered modal-confirm">
          <div class="modal-content-success shadow">
            <div class="modal-header justify-content-center">
              <div class="icon-box">
                <i class="fa-solid fa-check"></i>
              </div>
            </div>
            <div class="modal-body text-center">
              <h5 class="success-message py-4">{{ successMessage }}</h5>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { loadStripe } from '@stripe/stripe-js';
  import Loader from './loader.vue';
  
  export default {
    name: 'TokenPurchaseModal',
    
    components: {
      Loader
    },
  
    props: {
      isOpen: {
        type: Boolean,
        default: false
      }
    },
  
    data() {
      return {
        loader: false,
        stripe: null,
        elements: null,
        cardNumberElement: null,
        cardExpiryElement: null,
        cardCvcElement: null,
        cardErrors: '',
        successMessage: '',
        showSuccessModal: false,
        
        // Token packages
        tokenPackages: [
          { tokens: 25000, amount: 1.00 },
          { tokens: 500000, amount: 5.00 },
        //   { tokens: 1000, amount: 18.00 },
        //   { tokens: 2500, amount: 40.00 },
        //   { tokens: 5000, amount: 75.00 }
        ],
        
        selectedPackage: null,
        
        // Access token from your auth system
        accessToken: localStorage.getItem('token') || ''
      };
    },
  
    watch: {
      async isOpen(newVal) {
        if (newVal) {
          // Initialize Stripe when modal opens
          await this.$nextTick();
          await this.initializeStripe();
        } else {
          // Cleanup when modal closes
          this.cleanupStripe();
        }
      }
    },
  
    methods: {
      async initializeStripe() {
        try {
          // Initialize Stripe with your publishable key
          this.stripe = await loadStripe('pk_test_51QT0q7HWIkcPssBR2luiS7D5bDQmf9arp31g3OHCwBCG6rwgn9Sp5cCE7vMrPQTdj5vb2yukCNAFSdFioDIAbkTn00wKWX60cQ');
          this.elements = this.stripe.elements();
  
          // Create card elements with unique IDs for modal
          this.cardNumberElement = this.elements.create('cardNumber');
          this.cardNumberElement.mount('#card-number-element-modal');
  
          this.cardExpiryElement = this.elements.create('cardExpiry');
          this.cardExpiryElement.mount('#card-expiry-element-modal');
  
          this.cardCvcElement = this.elements.create('cardCvc');
          this.cardCvcElement.mount('#card-cvc-element-modal');
  
          // Listen for errors
          this.cardNumberElement.on('change', (event) => {
            if (event.error) {
              this.cardErrors = event.error.message;
            } else {
              this.cardErrors = '';
            }
          });
        } catch (error) {
          console.error('Error initializing Stripe:', error);
          this.cardErrors = 'Failed to initialize payment system.';
        }
      },
  
      cleanupStripe() {
        // Unmount Stripe elements
        if (this.cardNumberElement) {
          this.cardNumberElement.unmount();
        }
        if (this.cardExpiryElement) {
          this.cardExpiryElement.unmount();
        }
        if (this.cardCvcElement) {
          this.cardCvcElement.unmount();
        }
        
        // Reset form
        this.selectedPackage = null;
        this.cardErrors = '';
      },
  
      onPackageChange() {
        this.cardErrors = '';
      },
  
      closeModal() {
        this.$emit('close');
      },
  
      async submitPayment() {
        if (!this.selectedPackage) {
          this.cardErrors = 'Please select a token package.';
          return;
        }
  
        try {
          this.loader = true;
          this.cardErrors = '';
  
          // Create payment method
          const { paymentMethod, error } = await this.stripe.createPaymentMethod({
            type: 'card',
            card: this.cardNumberElement,
          });
  
          if (error) {
            this.cardErrors = error.message;
            this.loader = false;
            return;
          }
  
          // Send payment data to backend
          await this.sendTokenPurchaseToBackend(paymentMethod.id);
  
        } catch (error) {
          console.error('Error:', error);
          this.cardErrors = error.message || 'An unexpected error occurred.';
          this.loader = false;
        }
      },
  
      async sendTokenPurchaseToBackend(paymentMethodId) {
        try {
          const response = await fetch('https://stratabackend.dotlinkertech.com/api/chatbot/add-tokens', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.accessToken}`
            },
            body: JSON.stringify({
              tokens: this.selectedPackage.tokens,
              amount: this.selectedPackage.amount,
              paymentMethodId: paymentMethodId
            })
          });
  
          const data = await response.json();
  
          if (!response.ok) {
            throw new Error(data.message || 'Payment failed');
          }
  
          // Payment successful
          this.loader = false;
          this.successMessage = `Successfully purchased ${this.selectedPackage.tokens} tokens!`;
          this.showSuccessModal = true;
  
          // Close modals and emit success event after 2 seconds
          setTimeout(() => {
            this.showSuccessModal = false;
            this.closeModal();
            this.$emit('success', {
              tokens: this.selectedPackage.tokens,
              amount: this.selectedPackage.amount
            });
          }, 2000);
  
        } catch (error) {
          this.loader = false;
          this.cardErrors = error.message || 'Failed to process payment. Please try again.';
          console.error('Backend error:', error);
        }
      }
    }
  };
  </script>
  
  <style scoped>
  /* Modal Backdrop */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1040;
    animation: fadeIn 0.3s ease;
  }
  
  /* Token Modal */
  .token-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1050;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    animation: slideIn 0.3s ease;
  }
  
  .modal-content-wrapper {
    background: #fff;
    border-radius: 12px;
    padding: 30px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    position: relative;
  }
  
  /* Close Button */
  .close-btn {
    position: absolute;
    top: 15px;
    right: 15px;
    background: transparent;
    border: none;
    font-size: 24px;
    color: #999;
    cursor: pointer;
    transition: color 0.3s;
    z-index: 10;
  }
  
  .close-btn:hover {
    color: #333;
  }
  
  /* Form Styles */
  .payment-form {
    display: flex;
    flex-direction: column;
  }
  
  h4 {
    font-family: "DM Sans", sans-serif;
    font-weight: 700;
    color: #333;
  }
  
  .form-group {
    margin-bottom: 20px;
  }
  
  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #333;
    font-size: 14px;
  }
  
  .form-control {
    width: 100%;
    padding: 12px;
    border: 2px solid #e1e1e1;
    border-radius: 8px;
    font-size: 14px;
    transition: border-color 0.3s;
  }
  
  .form-control:focus {
    outline: none;
    border-color: #5469D4;
  }
  
  .card-element {
    padding: 12px;
    border: 2px solid #e1e1e1;
    border-radius: 8px;
    background: #fff;
    transition: border-color 0.3s;
  }
  
  .card-element:focus-within {
    border-color: #5469D4;
  }
  
  /* Package Info */
  .package-info {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 20px;
  }
  
  .info-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  
  .info-row:last-child {
    margin-bottom: 0;
  }
  
  .info-row span {
    color: #666;
  }
  
  .info-row strong {
    color: #333;
    font-size: 16px;
  }
  
  /* Error Messages */
  .card-errors {
    color: #dc3545;
    font-size: 14px;
    margin-top: -10px;
    margin-bottom: 15px;
    padding: 10px;
    background: #ffe6e6;
    border-radius: 6px;
  }
  
  /* Submit Button */
  .submit-button {
    width: 100%;
    padding: 14px;
    background-color: #5469D4;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 600;
    margin-top: 10px;
    transition: background-color 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .submit-button:hover:not(:disabled) {
    background-color: #475cc8;
  }
  
  .submit-button:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  
  /* Success Modal */
  .success-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1060;
    animation: slideIn 0.3s ease;
  }
  
  .modal-confirm {
    color: #434e65;
    width: 90%;
    max-width: 450px;
    margin: 0 auto;
  }
  
  .modal-content-success {
    padding: 20px;
    font-size: 16px;
    border-radius: 12px;
    border: none;
    background: #fff;
  }
  
  .modal-header {
    background: #47c9a2;
    border-bottom: none;
    position: relative;
    text-align: center;
    margin: -20px -20px 0;
    border-radius: 12px 12px 0 0;
    padding: 35px;
  }
  
  .icon-box {
    color: #fff;
    width: 95px;
    height: 95px;
    display: inline-block;
    border-radius: 50%;
    z-index: 9;
    border: 5px solid #fff;
    padding: 15px;
    text-align: center;
    background: #47c9a2;
  }
  
  .icon-box i {
    font-size: 64px;
    line-height: 64px;
  }
  
  .success-message {
    color: #47c9a2;
    font-weight: 600;
    font-size: 18px;
  }
  
  /* Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translate(-50%, -60%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }
  
  /* Responsive */
  @media (max-width: 576px) {
    .token-modal {
      width: 95%;
    }
  
    .modal-content-wrapper {
      padding: 20px;
    }
  
    .close-btn {
      top: 10px;
      right: 10px;
    }
  }
  
  /* Scrollbar styling for modal */
  .token-modal::-webkit-scrollbar {
    width: 6px;
  }
  
  .token-modal::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  .token-modal::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  
  .token-modal::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
  </style>